import { ToolTip } from "@components/ToolTip";
import { Download, Visibility } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { CRM_ORDER_PAYMENT_TYPES } from "@utils/constants";
import { formatCurrency } from "@utils/formatCurrency";
import { useEffect, useState } from "react";
import { ModelInvoice } from "@sportsgravyengineering/sg-api-react-sdk";
import { useParams } from "react-router-dom";
import { TableView } from "@components/TableView";
import { getOrderBillingHistory } from "@services/Network";
import { capitalize } from "@utils/capitalize";
import { OrderInvoiceModal } from "./OrderInvoiceModal";
import { usePDF } from "@react-pdf/renderer";
import { InvoicePDF } from "@components/InvoicePDF";
import { Loader } from "@components/crud/Loader";
import { downloadPDFFromLink } from "@utils/downloadPdfFromLink";

const IconStyle = {
  height: "24px",
  width: "24px",
  color: "#666666"
};
const formatDateForDisplay = (dateStr) => {
  const inputDate = new Date(dateStr);
  const month = (inputDate.getMonth() + 1).toString().padStart(2, "0");
  const day = inputDate.getDate().toString().padStart(2, "0");
  const year = inputDate.getFullYear();
  const formattedDate = `${month}/${day}/${year}`;
  return formattedDate;
};

export const OrderBillingHistory = () => {
  const [invoiceToShow, setInvoiceToShow] = useState<ModelInvoice | null>(null);
  const [invoiceIdDownload, setInvoiceIdDownload] = useState<string | null>(
    null
  );
  const [invoiceInstance, setInvoiceInstance] = usePDF();
  const { orderId } = useParams();
  const getBillingHitory = (params, options) => {
    return getOrderBillingHistory(orderId, params, options);
  };

  const generateInvoiceInstance = async (invoiceToShow: ModelInvoice) => {
    return new Promise<void>((resolve) => {
      setInvoiceInstance(
        <InvoicePDF
          hasFooter={false}
          hasHeader={true}
          invoiceDetails={{
            from: {
              name: "Sports Gravy LLC.",
              address: " 9900 Hemingway Ave S, Cottage Grove, MN, 55016, USA"
            },
            invoiceNumber: invoiceToShow.invoiceId!,
            orderDate: invoiceToShow.order?.createdAt,
            paymentDate: invoiceToShow?.paymentDetail?.createdAt,
            paymentMode:
              CRM_ORDER_PAYMENT_TYPES.find(
                (f) => f.value === invoiceToShow.paymentDetail?.paymentType
              )?.label || "",
            terms: "Due Upon Receipt",
            paymentStatus:
              invoiceToShow.status === "COMPLETED" ? "PAID" : "FAILED",
            billTo: {
              to: invoiceToShow?.order?.account?.name as string,
              name:
                invoiceToShow.order?.contact?.firstName +
                " " +
                invoiceToShow.order?.contact?.lastName,
              email: invoiceToShow.order?.contact?.email as string,
              phone: invoiceToShow.order?.contact?.phone as string
            },
            items: [
              {
                name: invoiceToShow.order?.type,
                amount: invoiceToShow.amount!,
                quantity: 1,
                desciption: [
                  `Number of Yearly Athlete Registrations: ${invoiceToShow.order?.numberOfAthletes}`,
                  `$ ${invoiceToShow.order?.yearlyAmount} Per Athlete, Per Registration`
                ]
              },
              {
                name: "One time Setup Fee",
                quantity: 1,
                amount: 5000,
                desciption: []
              }
            ]
          }}
        />
      );
      resolve();
    });
  };

  const handleDownloadPDF = async (invoice: ModelInvoice) => {
    await generateInvoiceInstance(invoice);
  };

  useEffect(() => {
    if (invoiceIdDownload && invoiceInstance.url)
      downloadPDFFromLink(invoiceInstance.url, invoiceIdDownload);
  }, [invoiceIdDownload, invoiceInstance]);

  const COLUMNS: GridColDef<ModelInvoice>[] = [
    {
      field: "action",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        return (
          <div style={{ padding: "20px 0px", display: "flex" }}>
            <IconButton
              onClick={async () => {
                await generateInvoiceInstance(params.row!);
                if (!invoiceInstance.loading) {
                  setInvoiceToShow(params.row!);
                }
              }}
            >
              <ToolTip title="View Invoice" placement="top">
                <Visibility style={IconStyle} />
              </ToolTip>
            </IconButton>
            <Loader isLoading={invoiceInstance.loading}>
              <IconButton
                onClick={() => {
                  setInvoiceIdDownload(params.row.invoiceId!);
                  handleDownloadPDF(params.row!);
                }}
              >
                <ToolTip title="Download Invoice" placement="top">
                  <Download style={IconStyle} />
                </ToolTip>
              </IconButton>
            </Loader>
          </div>
        );
      }
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      flex: 1,
      minWidth: 170,
      valueGetter: ({ row }) => row.invoiceId!
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }) => formatCurrency(row.amount!)
    },
    {
      field: "paymentType",
      headerName: "Payment Type",
      flex: 1,
      minWidth: 150,

      valueGetter: ({ row }) =>
        CRM_ORDER_PAYMENT_TYPES.find(
          (o) => o.value === row.paymentDetail?.paymentType
        )?.label
    },
    {
      field: "dateSent",
      headerName: "Date Sent",
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }) => formatDateForDisplay(row.dateSent)
    },
    {
      field: "datePaid",
      headerName: "Date Paid",
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }) =>
        row.datePaid ? formatDateForDisplay(row.datePaid) : ""
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 150,
      valueGetter: ({ row }) => capitalize(row.status!)
    }
  ];

  return (
    <div>
      <TableView
        useGet={getBillingHitory}
        columns={COLUMNS}
        getRowId={(row) => row.invoiceId}
        title=""
        hasActionColumn={false}
        getRowHeight={() => "auto"}
        hideToolbar
        hideFooter
        hideFooterPagination
      />
      {invoiceToShow && (
        <OrderInvoiceModal
          open={!!invoiceToShow}
          invoice={invoiceToShow}
          invoiceInstance={invoiceInstance}
          onClose={() => setInvoiceToShow(null)}
        />
      )}
    </div>
  );
};
